import { useCallback } from "react";

import {
  CREATE_CUSTOM_ANNOTATION,
  DELETE_CUSTOM_ANNOTATION,
  GET_CUSTOM_ANNOTATIONS,
  UPDATE_CUSTOM_ANNOTATION,
} from "@gql/custom-annotations";
import {
  type QueryOptions,
  useNorthbeamLazyQuery,
  useNorthbeamMutation,
} from "@hooks/http";
import {
  type CreateCustomAnnotation,
  type CreateCustomAnnotationVariables,
  type DeleteCustomAnnotation,
  type DeleteCustomAnnotationVariables,
  type GetClientCustomCustomAnnotations,
  type GetClientCustomCustomAnnotationsVariables,
  type UpdateCustomAnnotation,
  type UpdateCustomAnnotationVariables,
} from "@north-beam/nb-client-side";

const getFetchOptions = (enableCache?: boolean) => {
  const fetchOptions: QueryOptions<GetClientCustomCustomAnnotations> =
    enableCache
      ? { fetchPolicy: "cache-first" }
      : { fetchPolicy: "network-only", nextFetchPolicy: "no-cache" };

  return fetchOptions;
};

export const useAnnotations = (enableCache?: boolean) => {
  const {
    loadData: fetchAnnotations,
    data: { loading: isAnnotationsLoading, data },
  } = useNorthbeamLazyQuery<
    GetClientCustomCustomAnnotations,
    GetClientCustomCustomAnnotationsVariables
  >(GET_CUSTOM_ANNOTATIONS, { ...getFetchOptions(enableCache) });

  const [createAnnotationMutation, { loading: isCreateAnnotationLoading }] =
    useNorthbeamMutation<
      CreateCustomAnnotation,
      CreateCustomAnnotationVariables
    >(CREATE_CUSTOM_ANNOTATION);

  const createAnnotation = useCallback(
    async (description: string, annotatedDate: string) =>
      createAnnotationMutation({ variables: { description, annotatedDate } }),
    [createAnnotationMutation],
  );

  const [updateAnnotationMutation, { loading: isUpdateAnnotationLoading }] =
    useNorthbeamMutation<
      UpdateCustomAnnotation,
      UpdateCustomAnnotationVariables
    >(UPDATE_CUSTOM_ANNOTATION);

  const updateAnnotation = useCallback(
    async (id: string, description: string) =>
      updateAnnotationMutation({ variables: { id, description } }),
    [updateAnnotationMutation],
  );

  const [deleteAnnotationMutation, { loading: isDeleteAnnotationLoading }] =
    useNorthbeamMutation<
      DeleteCustomAnnotation,
      DeleteCustomAnnotationVariables
    >(DELETE_CUSTOM_ANNOTATION);

  const deleteAnnotation = useCallback(
    async (id: string) => deleteAnnotationMutation({ variables: { id } }),
    [deleteAnnotationMutation],
  );

  return {
    annotations: data?.me?.clientCustomAnnotations || [],
    isAnnotationsLoading:
      isAnnotationsLoading ||
      isCreateAnnotationLoading ||
      isUpdateAnnotationLoading ||
      isDeleteAnnotationLoading,
    fetchAnnotations,
    createAnnotation,
    deleteAnnotation,
    updateAnnotation,
  };
};
